import React, { FC, ReactElement } from 'react';
import {
  SupplierValidationDocument,
  SupplierValidationRecord,
  supplierValidationDocumentTypeText,
} from '@mortee/domain/validationSystem';
import styled from '@emotion/styled';
import useInfraStores from '@app/hooks/useInfraStores';
import { extractLogErrorIdFromError } from '@app/libs/request';
import Loadable from '@app/utils/Loadable';
import { formattedDateOptionsDDMMYYYY } from '@app/utils/timeUtils';
import { TransparentCard } from '@mortee/routes/validationSystem/editValidtionRecord/EditValidationRecordStyles';
import LoadableTransitionLoader from '@app/components/LoadableTransitionLoader';
import MorteeMode from '@mortee/morteeMode';

interface Props {
  record: SupplierValidationRecord;
  documents: Loadable<SupplierValidationDocument[]>;
  onDocumentClick?(document: SupplierValidationDocument): void;
}

function formatFileSize(bytes: number): string {
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} KB`;
  }
  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

const SupplierValidationRecordDocuments: FC<Props> = ({ record, documents, onDocumentClick }) => {
  const { localeStore, navigationStore } = useInfraStores<MorteeMode>();
  const { staticId } = record;

  function openDocumentViewer(document: SupplierValidationDocument): void {
    // Opened synchronously, inside the click gesture, so browsers don't treat it as a blocked popup.
    window.open(navigationStore.generateValidationRecordDocumentViewerHref(staticId, document.id), '_blank');
    onDocumentClick?.(document);
  }

  if (documents.isRejected()) {
    const error = documents.stateMetadata?.error;

    return (
      <TransparentCard>
        <div data-testid='documents-load-error'>
          Error loading documents, refer to error code: {extractLogErrorIdFromError(error)}
        </div>
      </TransparentCard>
    );
  }

  return (
    <LoadableTransitionLoader loadable={documents}>
      {(documentList): ReactElement => {
        if (documentList.length === 0) {
          return (
            <TransparentCard>
              <div data-testid='no-documents-found'>No documents found for this record</div>
            </TransparentCard>
          );
        }

        return (
          <DocumentsContainer>
            {documentList.map((document, index) => (
              <DocumentRow
                key={document.id}
                type='button'
                data-testid={`document-row-${index}`}
                onClick={(): void => openDocumentViewer(document)}
              >
                <DocumentFileName data-testid={`document-file-name-${index}`}>{document.originalFileName}</DocumentFileName>
                <DocumentCell data-testid={`document-type-${index}`}>
                  {supplierValidationDocumentTypeText[document.documentType]}
                </DocumentCell>
                <DocumentCell data-testid={`document-date-${index}`}>
                  {localeStore.formatDateTime(document.writeTimestamp, formattedDateOptionsDDMMYYYY)}
                </DocumentCell>
                <DocumentCell data-testid={`document-size-${index}`}>{formatFileSize(document.fileSizeBytes)}</DocumentCell>
              </DocumentRow>
            ))}
          </DocumentsContainer>
        );
      }}
    </LoadableTransitionLoader>
  );
};

export default SupplierValidationRecordDocuments;

const DocumentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DocumentRow = styled.button`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid var(--transparent-gray-150);
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:hover {
    background-color: var(--transparent-black-50);
  }
`;

const DocumentFileName = styled.div`
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const DocumentCell = styled.div`
  overflow-wrap: anywhere;
`;
